import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import type { InterviewCreationFailedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewCreationFailed';
import CreateTextInterview from '@Application/Beneficiaries/Interviews/Commands/CreateTextInterview';
import createInterview from '@Application/Beneficiaries/Interviews/createInterview';
import generateClientReference from '@Application/Beneficiaries/Interviews/generateClientReference';
import PlainDate from '@Domain/PlainDate';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import EnteredTextFormData from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GatherSection/Sheets/EnterTextSheet/FormData/EnteredTextFormData';
import PreparationSheet from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Sheets/PreparationSheet';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { css } from '@emotion/react';
import { Check } from 'lucide-react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useDispatch } from '@verclam/synapse';
import { Button, Form, Message, TextArea } from '@verclam/ui-kit';

const formStyle = css`
	--ui-kit-form-gap: 0.75rem;
`;

const OFFLINE_FALLBACK_MESSAGE = "Le texte n'a pas pu être envoyé. Vérifiez votre connexion puis réessayez.";
const SERVER_FALLBACK_MESSAGE = "Le texte n'a pas pu être envoyé. Réessayez dans un instant.";

type Props = {
	readonly subject: SubjectAddress;
	readonly preparationId: number;
	readonly visible: boolean;
	readonly onHide: () => void;
};

/**
 * « Saisir un texte »: a text typed or pasted, sent as is and turned into a transcript by the server.
 * The text and the `clientReference` survive a failure and a close, so a retry never creates a
 * duplicate; both are renewed after a success.
 */
const EnterTextSheet: React.FC<Props> = ({ subject, preparationId, visible, onHide }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();

	const [formData, setFormData] = React.useState(() => EnteredTextFormData.createEmpty());
	const [clientReference, setClientReference] = React.useState(() => generateClientReference());
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	const handleTextChange = React.useCallback((value: string) => {
		setFormData((current) => current.setText(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		const today = PlainDate.today();

		setIsSubmitting(true);
		setErrorMessage(null);

		dispatch(
			createInterview(
				new CreateTextInterview(
					currentFacility.id,
					subject,
					clientReference,
					`Texte saisi du ${today.toDisplay()}`,
					today,
					formData.text,
					preparationId,
				),
			),
		)
			.then(() => {
				setFormData(EnteredTextFormData.createEmpty());
				setClientReference(generateClientReference());
				onHide();
			})
			.catch((action: InterviewCreationFailedAction) => {
				updateValidationFromExternalSource({
					action,
					eventNames: 'INTERVIEW_CREATION_FAILED',
					formDataUpdater: setFormData,
				});
				setErrorMessage(
					action.payload?.message ??
						(action.status === null ? OFFLINE_FALLBACK_MESSAGE : SERVER_FALLBACK_MESSAGE),
				);
			})
			.finally(() => setIsSubmitting(false));
	}, [formData, dispatch, currentFacility.id, subject, clientReference, preparationId, onHide]);

	return (
		<PreparationSheet
			visible={visible}
			onHide={onHide}
			header="Saisir un texte"
			subtitle="Un compte rendu, un mail ou des notes prises sur papier. Rien n'est généré."
		>
			<Form css={formStyle} onSubmit={handleSubmit} aria-label="Saisir un texte">
				<TextArea
					name="text"
					label="Texte"
					required
					autoResize
					rows={8}
					value={formData.text}
					onChange={handleTextChange}
					errorMessage={formData.getValidationMessage('text')}
					isFormFieldValid={formData.isValid('text')}
				/>
				<ConditionalLoader data={errorMessage}>
					{(message) => <Message severity="error" text={message} />}
				</ConditionalLoader>
				<Button
					type="submit"
					label="Ajouter à la préparation"
					icon={<Check size={16} aria-hidden="true" />}
					loading={isSubmitting}
				/>
			</Form>
		</PreparationSheet>
	);
};

export default EnterTextSheet;
